if (!customElements.get('product-similar-selector')) {
  customElements.define(
    'product-similar-selector',
    class ProductSimilarSelector extends HTMLElement {
      static cache = new Map();
      static requests = new Map();
      static popstateBound = false;

      connectedCallback() {
        this.handleClick = this.onClick.bind(this);
        this.handlePointerOver = this.onPointerOver.bind(this);
        this.handleFocusIn = this.onFocusIn.bind(this);

        this.addEventListener('click', this.handleClick);
        this.addEventListener('pointerover', this.handlePointerOver);
        this.addEventListener('focusin', this.handleFocusIn);

        this.snapshotCurrentPage();
        this.prefetchIdle();
        this.bindPopState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('pointerover', this.handlePointerOver);
        this.removeEventListener('focusin', this.handleFocusIn);
      }

      get productInfo() {
        return this.closest('product-info');
      }

      get canSwapInPlace() {
        const productInfo = this.productInfo;
        return Boolean(
          productInfo &&
            productInfo.dataset.updateUrl === 'true' &&
            typeof productInfo.swapToProduct === 'function' &&
            !window.Shopify?.designMode
        );
      }

      get optionLinks() {
        return Array.from(this.querySelectorAll('a.product-similar-selector__option[href]'));
      }

      bindPopState() {
        if (ProductSimilarSelector.popstateBound) return;
        ProductSimilarSelector.popstateBound = true;
        window.addEventListener('popstate', ProductSimilarSelector.onPopState);
      }

      static onPopState() {
        if (!/\/products\//.test(window.location.pathname)) {
          window.location.reload();
          return;
        }

        const productInfo = document.querySelector('product-info[data-update-url="true"]');
        if (!productInfo || typeof productInfo.swapToProduct !== 'function') {
          window.location.reload();
          return;
        }

        const url = ProductSimilarSelector.normalizeUrl(window.location.href);
        ProductSimilarSelector.loadHtml(url)
          .then((html) => productInfo.swapToProduct(url, { html, historyMode: 'none' }))
          .catch(() => {
            window.location.reload();
          });
      }

      static normalizeUrl(url) {
        try {
          return new URL(url, window.location.origin).pathname;
        } catch (error) {
          return url;
        }
      }

      static serializeCurrentPage() {
        const main = document.querySelector('main');
        const stickyAtc = document.getElementById('shopify-section-sticky-atc-bar');
        if (!main) return '';

        return `<!DOCTYPE html><html><head>${document.head.innerHTML}</head><body>${main.outerHTML}${
          stickyAtc ? stickyAtc.outerHTML : ''
        }</body></html>`;
      }

      static parseHtml(markup) {
        return markup instanceof Document ? markup : new DOMParser().parseFromString(markup, 'text/html');
      }

      static loadHtml(url) {
        const cacheKey = ProductSimilarSelector.normalizeUrl(url);
        if (ProductSimilarSelector.cache.has(cacheKey)) {
          return Promise.resolve(ProductSimilarSelector.parseHtml(ProductSimilarSelector.cache.get(cacheKey)));
        }

        if (ProductSimilarSelector.requests.has(cacheKey)) {
          return ProductSimilarSelector.requests.get(cacheKey);
        }

        const request = fetch(cacheKey)
          .then((response) => {
            if (!response.ok) throw new Error(`Failed to load ${cacheKey}`);
            return response.text();
          })
          .then((responseText) => {
            ProductSimilarSelector.cache.set(cacheKey, responseText);
            ProductSimilarSelector.requests.delete(cacheKey);
            return ProductSimilarSelector.parseHtml(responseText);
          })
          .catch((error) => {
            ProductSimilarSelector.requests.delete(cacheKey);
            throw error;
          });

        ProductSimilarSelector.requests.set(cacheKey, request);
        return request;
      }

      snapshotCurrentPage(force = false) {
        const productUrl = this.productInfo?.dataset.url;
        if (!productUrl) return;

        const cacheKey = ProductSimilarSelector.normalizeUrl(productUrl);
        if (!force && ProductSimilarSelector.cache.has(cacheKey)) return;

        const snapshot = ProductSimilarSelector.serializeCurrentPage();
        if (snapshot) ProductSimilarSelector.cache.set(cacheKey, snapshot);
      }

      prefetch(url) {
        if (!url || !this.canSwapInPlace) return;
        ProductSimilarSelector.loadHtml(url).catch(() => {});
      }

      prefetchIdle() {
        if (!this.canSwapInPlace) return;

        const prefetchAll = () => {
          this.optionLinks.forEach((link) => this.prefetch(link.href));
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(prefetchAll, { timeout: 2500 });
        } else {
          setTimeout(prefetchAll, 1500);
        }
      }

      onPointerOver(event) {
        const link = event.target.closest('a.product-similar-selector__option[href]');
        if (!link) return;
        this.prefetch(link.href);
      }

      onFocusIn(event) {
        const link = event.target.closest('a.product-similar-selector__option[href]');
        if (!link) return;
        this.prefetch(link.href);
      }

      onClick(event) {
        const link = event.target.closest('a.product-similar-selector__option[href]');
        if (!link || !this.canSwapInPlace) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.defaultPrevented) return;

        event.preventDefault();
        this.swapTo(link);
      }

      swapTo(link) {
        const productInfo = this.productInfo;
        const url = ProductSimilarSelector.normalizeUrl(link.href);
        if (!productInfo || !url) return;

        this.snapshotCurrentPage(true);
        this.setLoading(true, link);

        ProductSimilarSelector.loadHtml(url)
          .then((html) => {
            if (!html.querySelector('main') || !html.querySelector('product-info')) {
              throw new Error('Invalid product page');
            }
            return productInfo.swapToProduct(url, { html, historyMode: 'push' });
          })
          .catch(() => {
            window.location.href = link.href;
          })
          .finally(() => {
            this.setLoading(false, link);
          });
      }

      setLoading(isLoading, link) {
        this.classList.toggle('is-loading', isLoading);
        this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.productInfo?.classList.toggle('is-swapping', isLoading);
        link?.classList.toggle('is-loading', isLoading);
      }
    }
  );
}
